import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Form,
  Input,
  Button,
  Space,
  Select,
  Row,
  Col,
  DatePicker,
  Tag,
  Tooltip,
  Divider,
  message,
} from "antd";
import type { TableProps } from "antd";
import request from "../../../utils/request";
import { SummaryListItem, TabProps } from "../../../types/dataControl";
import SummaryDetailModal from "./modals/SummaryDetailModal";

const { Option } = Select;
const { RangePicker } = DatePicker;

const SummaryTab: React.FC<TabProps> = ({ API_BASE_URL, token }) => {
  const [summaryList, setSummaryList] = useState<SummaryListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [selectedSummary, setSelectedSummary] =
    useState<SummaryListItem | null>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [form] = Form.useForm();

  // 获取摘要列表
  const fetchSummaryList = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const params: any = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      // 添加筛选条件
      if (values.open_id) params.open_id = values.open_id;
      if (values.project_key) params.project_key = values.project_key;
      if (values.refer) params.refer = values.refer;
      if (values.title) params.title = values.title;
      if (values.url) params.url = values.url;
      if (values.is_vector !== undefined) params.is_vector = values.is_vector;
      if (values.doc_type) params.doc_type = values.doc_type;
      if (values.language) params.language = values.language;

      // 处理时间范围
      if (values.dateRange && values.dateRange[0] && values.dateRange[1]) {
        params.start_time = values.dateRange[0].format("YYYY-MM-DD");
        params.end_time = values.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await request.get<{
        code: number;
        msg: string;
        data: { total: number; list: SummaryListItem[] };
      }>(`${API_BASE_URL}/api/v3/admin/user/summaryList`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.code === 200) {
        setSummaryList(response.data.data.list);
        setTotal(response.data.data.total);
      } else {
        message.error(response.data.msg || "获取摘要列表失败");
      }
    } catch (error) {
      console.error("获取摘要列表失败:", error);
      message.error("获取摘要列表失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, token, form, pagination]);

  // 初始加载数据
  useEffect(() => {
    fetchSummaryList();
  }, [fetchSummaryList]);

  // 处理表格变化
  const handleTableChange: TableProps<SummaryListItem>["onChange"] = (
    pagination
  ) => {
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  // 处理查询
  const handleSearch = () => {
    setPagination({
      ...pagination,
      current: 1,
    });
    fetchSummaryList();
  };

  // 重置筛选
  const handleReset = () => {
    form.resetFields();
    setPagination({
      current: 1,
      pageSize: 10,
    });
    fetchSummaryList();
  };

  // 查看摘要详情
  const viewDetail = (record: SummaryListItem) => {
    setSelectedSummary(record);
    setModalVisible(true);
  };

  // 定义表格列
  const columns: TableProps<SummaryListItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "用户ID/邮箱",
      key: "user",
      width: 180,
      render: (_, record) => (
        <div>
          <div>UID: {record.open_id}</div>
          <div>{record.email !== "NULL" ? record.email : "-"}</div>
        </div>
      ),
    },
    {
      title: "项目Key",
      dataIndex: "project_key",
      key: "project_key",
      width: 180,
      ellipsis: true,
    },
    {
      title: "标题",
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (title) => (
        <Tooltip title={title}>
          <span>{title}</span>
        </Tooltip>
      ),
    },
    {
      title: "摘要",
      dataIndex: "summary",
      key: "summary",
      ellipsis: {
        showTitle: false,
      },
      render: (summary) => (
        <Tooltip title={summary}>
          <span>{summary}</span>
        </Tooltip>
      ),
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (url) => (
        <Tooltip title={url}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        </Tooltip>
      ),
    },
    {
      title: "生成时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (date) => new Date(date).toLocaleString("zh-CN"),
    },
    {
      title: "属性",
      key: "properties",
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Tag color={record.is_vector ? "green" : "default"}>
            {record.is_vector ? "已向量化" : "未向量化"}
          </Tag>
          <Tag color="blue">{record.language}</Tag>
          <Tag color="purple">{record.doc_type}</Tag>
        </Space>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => viewDetail(record)}
          style={{ padding: 0 }}
        >
          查看详情
        </Button>
      ),
    },
  ];

  return (
    <>
      {/* 摘要筛选表单 */}
      <Form form={form} layout="vertical" onFinish={handleSearch}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="用户ID" name="open_id">
              <Input placeholder="请输入用户ID" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="项目Key" name="project_key">
              <Input placeholder="请输入项目Key" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="标题" name="title">
              <Input placeholder="请输入标题" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="URL" name="url">
              <Input placeholder="请输入URL" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="refer" name="refer">
              <Input placeholder="请输入refer" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="向量化状态" name="is_vector">
              <Select placeholder="请选择" allowClear>
                <Option value={true}>已向量化</Option>
                <Option value={false}>未向量化</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="文档类型" name="doc_type">
              <Select placeholder="请选择" allowClear>
                <Option value="summary">摘要</Option>
                <Option value="file">文件</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="语言" name="language">
              <Input placeholder="请输入语言代码" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8}>
            <Form.Item label="时间范围" name="dateRange">
              <RangePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Form.Item label=" " colon={false}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={
                    <i className="ri-search-line" style={{ marginRight: 4 }} />
                  }
                >
                  查询
                </Button>
                <Button
                  onClick={handleReset}
                  icon={
                    <i className="ri-refresh-line" style={{ marginRight: 4 }} />
                  }
                >
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider style={{ margin: "12px 0 24px" }} />

      {/* 摘要列表表格 */}
      <Table
        columns={columns}
        dataSource={summaryList}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1100 }}
      />

      {/* 摘要详情模态框 */}
      <SummaryDetailModal
        visible={modalVisible}
        data={selectedSummary}
        onClose={() => setModalVisible(false)}
      />
    </>
  );
};

export default SummaryTab;
